import { ReactNode, SubmitEventHandler, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { ui } from '@procuros/datachecks';

import { SchemableFormNavigationContextProvider } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContextProvider';
import { useIssueResolver } from 'hooks/useIssueResolver';
import { mapVirtualToActualDocumentType } from 'services/documentTypes/documentTypes';
import { DisplayModes } from 'services/webedi/types';
import { WebEDIErrorsServiceSingleton } from 'services/webediErrors';
import { handleSubmitAllowingHiddenErrors } from 'services/webediErrors/hiddenErrorsSubmission';
import { useFocusErrorOnMount } from 'services/webediErrors/hooks';
import { useNavigableAnnotations } from 'services/webediErrors/useNavigableAnnotations';
import { setIsValidating, useWebEDIStore } from 'stores/webedi/webedi';
import { DATA_CHECK_ERROR_CODE, EMPTY_FUNCTION } from 'support/helpers/const/const';
import { routeToTransactionPage } from 'support/helpers/navigation/navigation';
import { isCurrentPartnerTheMessageReceiver } from 'support/helpers/relationships/relationships';
import { datachecksResolver } from 'support/helpers/resolvers/datachecksResolver';
import { buildDocumentValidationKey } from 'support/helpers/resolvers/documentValidationKey';
import {
  DocumentType,
  DocumentTypeWithVirtual,
  MessageState,
  MessageV2DTO,
  PartnerDTO,
  ProcessSpecificationDTO,
  TransactionInternalType,
  UserDTO,
} from 'support/types';
import { Form } from 'components/Form/Form';
import { LoadingOverlay } from 'components/Loading/LoadingOverlay';
import { Page, PAGE_SECTION_PORTAL_ROOT_ID } from 'components/Page/Page';
import { Placeholder } from 'components/Placeholder/Placeholder';
import { DocumentSections } from 'components/WebEDI/DocumentSections/DocumentSections';
import { FieldAnnotationPopover } from 'components/WebEDI/FieldAnnotationPopover/FieldAnnotationPopover';
import { HiddenErrorsAdminBanner } from 'components/WebEDI/WebEDIMessageErrorBanner/components';
import { WebEDIMessageErrorBanner } from 'components/WebEDI/WebEDIMessageErrorBanner/WebEDIMessageErrorBanner';

import { Datachecks } from '../components/Datachecks/Datachecks';
import { CreateEditDocumentBottomBanner } from '../CreateEditDocumentBottomBanner';

const DISPLAY_MODE = DisplayModes.edit;

export type EditMessageContentProps = {
  message: MessageV2DTO | undefined;
  data: Record<string, unknown>;
  uiConfig: Array<ui.Section> | undefined;
  processSpecFields: ProcessSpecificationDTO['fields'] | undefined;
  currentPartner: PartnerDTO | undefined;
  currentUser: UserDTO | undefined;
  canEditMessage: boolean;

  baseData?: Record<string, unknown>;
  revisedData?: Record<string, unknown>;

  actionBar: ReactNode;
  sidebar?: ReactNode;
  modals?: ReactNode;

  onSubmit: (formValues: Record<string, unknown>) => void;
  isSubmitting: boolean;
};

export const EditMessageContent = ({
  message,
  data,
  uiConfig,
  processSpecFields,
  currentPartner,
  currentUser,
  canEditMessage,
  baseData,
  revisedData,
  actionBar,
  sidebar,
  modals,
  onSubmit,
  isSubmitting,
}: EditMessageContentProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useFocusErrorOnMount();
  const {
    annotations,
    blocking_error: blockingError,
    canonical,
    id: messageId,
    issue_resolver_id: issueResolverId,
    receiver_partner: receiverPartner,
    relationship_id: relationshipId,
    sender_partner: senderPartner,
    state: messageState,
    type: documentType,
  } = message ?? {};
  const issueResolver = useIssueResolver(issueResolverId);

  const { formMethods, isBigMessage, isValidationComplete } = useEditDocumentForm({
    message,
    data,
    processSpecFields,
  });
  const { getValues } = formMethods;

  const handleNativeSubmission: SubmitEventHandler = (e) => {
    e.preventDefault();
    onSubmit(getValues());
  };

  const isBannerLoading = !isValidationComplete && isBigMessage;
  const hiddenErrors = useWebEDIStore((state) => state.hiddenErrors);

  useNavigableAnnotations({
    formMethods,
    uiConfig,
    mode: DISPLAY_MODE,
    variables: receiverPartner ? { receiverPartnerName: receiverPartner.name } : {},
    documentType: documentType as DocumentTypeWithVirtual | undefined,
    senderPartner,
    receiverPartner,
    annotations,
    canonical: canonical as Record<string, unknown> | undefined,
    flattenData: data,
    enabled: Boolean(canEditMessage && uiConfig),
  });

  return (
    <FormProvider {...formMethods}>
      <Datachecks targetRelationshipId={relationshipId} documentType={mapVirtualToActualDocumentType(documentType)} />
      <Page>
        <Form
          noValidate
          id="webedi-document"
          onSubmit={isBigMessage ? handleNativeSubmission : handleSubmitAllowingHiddenErrors(formMethods, onSubmit)}
        >
          <SchemableFormNavigationContextProvider
            service={WebEDIErrorsServiceSingleton}
            NavigableItemPopoverComponent={FieldAnnotationPopover}
          >
            <Page.Head rightContent={actionBar} />
            <Page.Section
              rightAside={sidebar}
              topBanner={
                (blockingError || (annotations && annotations.length > 0)) && !isSubmitting ? (
                  <WebEDIMessageErrorBanner
                    mode={DISPLAY_MODE}
                    blockingError={blockingError ?? undefined}
                    issueResolver={issueResolver}
                    senderPartner={senderPartner}
                    receiverPartner={receiverPartner}
                    isCurrentPartnerReceiver={isCurrentPartnerTheMessageReceiver(message, currentPartner?.id)}
                    isLoadingDatachecksErrors={isBannerLoading}
                    messageState={messageState}
                  />
                ) : null
              }
              bottomBanner={
                canEditMessage && uiConfig ? (
                  <CreateEditDocumentBottomBanner documentType={documentType as DocumentType} />
                ) : null
              }
            >
              <div className="relative">
                {!canEditMessage ? (
                  <Placeholder
                    title={t('webedi:editDocument.noActionRequired.title')}
                    description={t('webedi:editDocument.noActionRequired.description')}
                    icon={<LockClosedIcon />}
                    ctaText={t('webedi:editDocument.noActionRequired.ctaText')}
                    ctaOnClick={() => {
                      navigate(routeToTransactionPage(TransactionInternalType.message, messageId as string));
                    }}
                  />
                ) : uiConfig ? (
                  <>
                    {currentUser?.isAdmin && hiddenErrors.length > 0 ? (
                      <HiddenErrorsAdminBanner hiddenErrors={hiddenErrors} />
                    ) : null}
                    <DocumentSections
                      mode={DISPLAY_MODE}
                      uiConfig={uiConfig}
                      documentType={documentType as DocumentType}
                      variables={receiverPartner ? { receiverPartnerName: receiverPartner.name } : {}}
                      isSubmitting={isSubmitting}
                      baseData={baseData}
                      revisedData={revisedData}
                    />
                  </>
                ) : null}
              </div>
              <LoadingOverlay isVisible={isSubmitting} portalRootId={PAGE_SECTION_PORTAL_ROOT_ID} />
            </Page.Section>
          </SchemableFormNavigationContextProvider>
        </Form>
      </Page>
      {modals}
    </FormProvider>
  );
};

const useEditDocumentForm = ({
  message,
  processSpecFields,
  data,
}: {
  message: MessageV2DTO | undefined;
  processSpecFields: ProcessSpecificationDTO['fields'] | undefined;
  data: Record<string, unknown>;
}) => {
  const isBigMessage = useWebEDIStore((state) => state.isBigMessage);
  const isValidationComplete = useWebEDIStore((state) => state.isValidationComplete);
  const dataStores = useWebEDIStore((state) => state.dataStores);
  const shouldRemoveResolver = isBigMessage && isValidationComplete;
  const hasDatachecksError = message?.blocking_error?.code === DATA_CHECK_ERROR_CODE;

  const resolver = useMemo(
    () =>
      message?.internal_state === MessageState.routingFailed || shouldRemoveResolver
        ? undefined
        : datachecksResolver({
            fieldsConfiguration: processSpecFields,
            withTimeout: false,
            dataStores,
            documentKey: buildDocumentValidationKey({ messageId: message?.id }),
          }),
    [message?.internal_state, processSpecFields, shouldRemoveResolver, dataStores, message?.id],
  );

  const formMethods = useForm({
    defaultValues: data,
    values: data,
    resolver,
    mode: 'onSubmit',
    ...(isBigMessage && { reValidateMode: 'onSubmit' }),
  });

  useEffect(() => {
    if (hasDatachecksError && data) {
      setIsValidating(true);
      formMethods.handleSubmit(EMPTY_FUNCTION)();
    }
  }, [data, formMethods, hasDatachecksError, message]);

  return {
    formMethods,
    isBigMessage,
    isValidationComplete: hasDatachecksError ? isValidationComplete : true,
  };
};
